import { Stack, Typography, useTheme } from '@mui/material'
import { GroupOption } from '@/types/metrics'
import { formatter, getBottomMarginForCountChart } from '@/utils/chart'
import useTimeChartTicks from '@/hooks/useTimeChartTicks'
import BarChart, { BarChartProps } from './BarChart'

type TimeBarChartProps = {
  from: Date
  to: Date
  useRanges?: boolean
  dataSum?: number
  groupedBy: GroupOption
  yKey: string
} & Omit<BarChartProps, 'xKey' | 'keys'>

export default function TimeCountBarChart({
  from,
  to,
  data,
  dataSum,
  groupedBy,
  useRanges = true,
  yKey,
  height = 420,
  margin,
  ...props
}: TimeBarChartProps) {
  const theme = useTheme()
  const { axisBottom, includeYear, needsToRotate, showingRange } =
    useTimeChartTicks({
      from,
      to,
      dataLength: data.length,
      groupedBy,
      useRanges,
    })
  return (
    <BarChart
      enableLabel={false}
      colors={theme.palette.primary.main}
      data={data}
      xKey="date"
      keys={[yKey]}
      height={height}
      noData={dataSum === 0}
      axisBottom={axisBottom}
      axisLeft={{
        format: (e) => (Math.floor(e) === e ? e : ''),
        legendOffset: -50,
      }}
      margin={{
        bottom: getBottomMarginForCountChart(
          needsToRotate,
          includeYear,
          showingRange,
          groupedBy === 'hour',
        ),
        ...margin,
      }}
      tooltipContent={({ data, formattedValue }) => (
        <Stack sx={{ p: 1 }}>
          <Typography component="p" noWrap variant="caption">
            <b>Date:</b>{' '}
            {formatter(
              data.date as string,
              groupedBy,
              includeYear,
              to,
              useRanges,
            )}
          </Typography>
          <Typography component="p" noWrap variant="caption">
            <b>Count:</b> {formattedValue}
          </Typography>
        </Stack>
      )}
      {...props}
    />
  )
}
